// Library Imports
import * as React from "react";
import {
  Modal,
  Grid,
  Link,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
} from "@mui/material";

// header import
import { getHeaders } from "../utils/HeaderHelper";
import { Link as RouterLink, useHistory } from "react-router-dom";

// app setting imports
import { AuthenticationContext } from "../Context";
import GeneralModal from "../modals/GeneralModal";
import OverWriteTemplateModal from "../modals/OverWriteTemplateModal";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

interface Props {
  templateModalOpen: boolean;
  setTemplateModalOpen: any;
  setTemplate: any;
  setUnsavedChanges: any;
  checkTemplate: any;
  currentTemplate?: any;
  currentBlocks?: any[];
}

const NotesTemplateSelect: React.FC<Props> = (props: any) => {
  const [templates, setTemplates] = React.useState<any>([]);
  const [unsavedModalOpen, setUnsavedModalOpen] =
    React.useState<boolean>(false);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const [selectedTemplate, setSelectedTemplate] = React.useState<any>();
  const [searchKey, setSearchKey] = React.useState<string>(undefined);
  const history = useHistory();

  const getTemplates = () => {
    fetch(`/data_fetching/notes_templates?status=published`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setTemplates(result.resource.notes_templates);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const applyTemplate = () => {
    if (!selectedTemplate) {
      return;
    }
    // Fill in notes with the given block values
    props.setTemplateModalOpen(false);
    if (props.checkTemplate()) {
      setUnsavedModalOpen(true);
    } else {
      props.setTemplate(selectedTemplate);
    }
  };

  const reDirect = () => {
    window.open("/new-note-template", "_blank", "noopener");
  };

  const handleContinue = () => {
    setUnsavedModalOpen(false);
    props.setTemplate(selectedTemplate);
  };

  const handleCancel = () => {
    setUnsavedModalOpen(false);
  };

  React.useEffect(() => {
    getTemplates();
  }, []);

  const identifyCurrentTemplate = (currentBlocks, availableTemplates) => {
    if (!currentBlocks || !availableTemplates || currentBlocks.length === 0) {
      return null;
    }

    return availableTemplates.find((template) => {
      if (!template.blocks || template.blocks.length !== currentBlocks.length) {
        return false;
      }

      return template.blocks.every((templateBlock, index) => {
        const currentBlock = currentBlocks[index];
        return (
          templateBlock.note === currentBlock.note &&
          templateBlock.order === currentBlock.order
        );
      });
    });
  };

  // Set the current template as selected when modal opens
  React.useEffect(() => {
    if (props.templateModalOpen) {
      if (props.currentTemplate) {
        setSelectedTemplate(props.currentTemplate);
      } else {
        const identifiedTemplate = identifyCurrentTemplate(
          props.currentBlocks,
          templates
        );
        setSelectedTemplate(identifiedTemplate || null);
      }
    }
  }, [props.templateModalOpen, props.currentTemplate, templates]);

  const filteredTemplates = templates?.filter((template) => {
    if (!searchKey) return true;
    const key = searchKey.toLowerCase();
    return (
      template.name?.toLowerCase().includes(key) ||
      template.subtext?.toLowerCase().includes(key)
    );
  });

  return (
    <>
      {unsavedModalOpen && (
        <OverWriteTemplateModal
          open={unsavedModalOpen}
          setUnsavedModalOpen={setUnsavedModalOpen}
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      )}
      {templates.length > 0 ? (
        <Modal open={props.templateModalOpen} className="follow-up-modal">
          <div className="paper" style={{ width: "900px", padding: "0px" }}>
            <div className="paperInner" style={{ padding: "0px" }}>
              <Grid
                container
                className="fum-header-container"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h1 className="fum-header">Choose Encounter Template</h1>
                <Link
                  onClick={() => props.setTemplateModalOpen(false)}
                  style={{ color: "black", paddingRight: "20px" }}
                >
                  <CloseIcon />
                </Link>
              </Grid>
              <div className="divider-orange" style={{ margin: "0px" }}></div>

              <Grid
                container
                style={{ padding: "24px" }}
                alignItems={"center"}
                spacing={4}
              >
                <Grid
                  item
                  xs={12}
                  md={5}
                  style={{
                    position: "relative",
                    marginLeft: "0px !important",
                  }}
                >
                  <FormControl sx={{ width: "100%" }} variant="outlined">
                    <OutlinedInput
                      id="outlined-adornment-weight"
                      placeholder="Search Templates"
                      endAdornment={
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      style={{ backgroundColor: "#EFE9E7", border: "none" }}
                      value={searchKey}
                      onChange={(event) => {
                        setSearchKey(event.target.value);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={7} className="subdued-text">
                  {
                    "Can't find the template you're looking for? Create one in the"
                  }{" "}
                  <a href="/templates-list">Template Builder</a>
                </Grid>
              </Grid>

              <TableContainer
                className="template-container"
                sx={{ maxHeight: "50vh" }}
              >
                <Table className="no-border-table">
                  <TableHead sx={{ borderBottom: "1.5px solid black" }}>
                    <TableRow>
                      <TableCell width={"1%"} />
                      <TableCell
                        className="bold-font-face nowrap-header"
                        style={{ cursor: "pointer" }}
                        sx={{ pl: "0" }}
                      >
                        <strong>Title</strong>
                      </TableCell>
                      <TableCell className="nowrap-header bolder-text">
                        Subtext
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates?.map((template) => (
                      <TableRow key={template?.id}>
                        <TableCell
                          width={"1%"}
                          align="right"
                          sx={{ pr: 0, pl: "24px" }}
                        >
                          <Radio
                            checked={selectedTemplate?.id === template.id}
                            value={template.id}
                            onChange={() => setSelectedTemplate(template)}
                          />
                        </TableCell>
                        <TableCell sx={{ pl: "0" }}>{template.name}</TableCell>
                        <TableCell>{template.subtext}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                style={{ padding: "30px" }}
              >
                <Grid
                  item
                  xs={4}
                  className="cancel-link-container"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Link
                    className="cancel-link"
                    onClick={() => props.setTemplateModalOpen(false)}
                  >
                    Cancel
                  </Link>
                </Grid>
                <Grid item xs={4} className="confirm-btn-container">
                  <Link
                    onClick={() => applyTemplate()}
                    className={`confirm-btn ${
                      !selectedTemplate ? "disabled" : ""
                    }`}
                    style={{
                      width: "135px",
                      opacity: selectedTemplate ? 1 : 0.5,
                      pointerEvents: selectedTemplate ? "auto" : "none",
                    }}
                  >
                    Continue
                  </Link>
                </Grid>
              </Grid>
            </div>
          </div>
        </Modal>
      ) : (
        <GeneralModal
          open={props.templateModalOpen}
          title="Create your First Template!"
          confirmButtonText="Go to Template Builder"
          successCallback={() => reDirect()}
          closeCallback={() => props.setTemplateModalOpen(false)}
          containerClassName="template-select-container template-container"
          width="500px"
        >
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            style={{ paddingTop: 20 }}
          >
            <Typography
              style={{
                fontWeight: "500",
                font: "18px QuicksandMedium",
              }}
            >
              It looks like you don`t have any templates yet. Head over to the
              Template Builder to create your first template!
            </Typography>
          </Grid>
        </GeneralModal>
      )}
    </>
  );
};

export default NotesTemplateSelect;
